/**
 * Canonical admin resource URLs (GDN-2723).
 *
 * Every resource is tenant-scoped — the Resource Access registry encodes this
 * directly (`list_capability_for` turns the `org` dimension on for every
 * non-global resource), so a resource URL always names an organization, and
 * optionally the user within it:
 *
 *   /admin-v2/organization/{orgId}/resources/{type}[/{id}]
 *   /admin-v2/organization/{orgId}/users/{userId}/resources/{type}[/{id}]
 *
 * Building these in one place is what lets the level (Org vs. User) be read
 * back off the route instead of guessed, and keeps the pre-consolidation
 * URL shapes (`/admin-v2/encounter/:id`, `?rtype=…&rid=…`) from creeping back
 * in as hand-written template strings.
 */

const ADMIN_ROOT = '/admin-v2';

/** Which level a resource URL is anchored at. */
export type ResourceLevel = 'org' | 'user';

export interface ResourceScope {
  organizationId: string;
  /** Present at the user level; absent at the org level. */
  userId?: string;
}

/** The level a scope represents — the sidebar's chip and the active rail entry. */
export function levelOf(scope: ResourceScope): ResourceLevel {
  return scope.userId ? 'user' : 'org';
}

/** Root of a scope's resource area: the type catalog. */
export function resourcesRootPath(scope: ResourceScope): string {
  const org = `${ADMIN_ROOT}/organization/${encodeURIComponent(scope.organizationId)}`;
  return scope.userId
    ? `${org}/users/${encodeURIComponent(scope.userId)}/resources`
    : `${org}/resources`;
}

/** Owner-scoped list for one resource type. */
export function resourceListPath(
  scope: ResourceScope,
  resourceType: string,
): string {
  return `${resourcesRootPath(scope)}/${encodeURIComponent(resourceType)}`;
}

/** One resource's detail view. */
export function resourceDetailPath(
  scope: ResourceScope,
  resourceType: string,
  resourceId: string,
): string {
  return `${resourceListPath(scope, resourceType)}/${encodeURIComponent(resourceId)}`;
}

/** The scope's owning entity page (where the context stack's current row goes). */
export function scopeHomePath(scope: ResourceScope): string {
  return scope.userId
    ? `${ADMIN_ROOT}/provider/${encodeURIComponent(scope.userId)}`
    : `${ADMIN_ROOT}/organization/${encodeURIComponent(scope.organizationId)}`;
}

/**
 * Read a scope back off route params. Returns null when the organization is
 * missing — resources have no org-less address, so there is nothing to render.
 */
export function parseResourceScope(params: {
  organizationId?: string;
  userId?: string;
}): ResourceScope | null {
  const organizationId = params.organizationId
    ? decodeURIComponent(params.organizationId)
    : '';
  if (!organizationId) return null;
  const userId = params.userId ? decodeURIComponent(params.userId) : undefined;
  return userId ? { organizationId, userId } : { organizationId };
}
